import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';

import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { Clause, Rule, OperatorOption } from '../../models/feature-flag-models';
import { LaunchDarklyService } from '../../services/launch-darkly.service';



@Component({
  selector: 'app-feature-flag-rules',
  standalone: true,
  imports: [
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    FormsModule
],
  template: `
    <div class="rules-container">
      <div class="rules-header">
        <h3>Targeting Rules</h3>
        <div class="header-actions">
          @if (enableAdding) {
            <button mat-button color="primary" (click)="addRule()">
              <mat-icon>add</mat-icon> Add Rule
            </button>
          }
          <button
            mat-raised-button
            color="primary"
            [disabled]="!hasChanges"
            (click)="saveChanges()"
            >
            Save Changes
          </button>
        </div>
      </div>
    
      @for (rule of workingRules; track rule; let i = $index) {
        <div class="rule-item">
          <mat-expansion-panel>
            <mat-expansion-panel-header>
              <mat-panel-title>
                Rule {{ i + 1 }}: {{ rule.description || 'No description' }}
              </mat-panel-title>
            </mat-expansion-panel-header>
            <div class="rule-content">
              <mat-form-field class="full-width">
                <mat-label>Description</mat-label>
                <input
                  matInput
                  [(ngModel)]="rule.description"
                  (change)="markAsChanged()"
                  placeholder="Rule description"
                  />
                </mat-form-field>
                <div class="clauses-container">
                  <h4>Clauses</h4>
                  @if (enableAdding) {
                    <button mat-button color="primary" (click)="addClause(rule)">
                      <mat-icon>add</mat-icon> Add Clause
                    </button>
                  }
                  @for (clause of rule.clauses; track clause; let j = $index) {
                    <div
                      class="clause-item"
                      >
                      <mat-form-field>
                        <mat-label>Attribute</mat-label>
                        <input
                          matInput
                          [(ngModel)]="clause.attribute"
                          (change)="markAsChanged()"
                          placeholder="Attribute"
                          [disabled]="!enableAdding"
                          />
                        </mat-form-field>
                        <mat-form-field>
                          <mat-label>Operator</mat-label>
                          <mat-select
                            [(ngModel)]="clause.op"
                            (selectionChange)="markAsChanged()"
                            [disabled]="!enableAdding"
                            >
                            @for (op of operators; track op) {
                              <mat-option [value]="op.value">
                                {{ op.label }}
                              </mat-option>
                            }
                          </mat-select>
                        </mat-form-field>
                        <mat-form-field class="values-field">
                          <mat-label>Values (comma-separated)</mat-label>
                          <input
                            matInput
                            [ngModel]="clause.values.join(',')"
                            (ngModelChange)="updateClauseValues(clause, $event)"
                            placeholder="Value1, Value2, ..."
                            />
                          </mat-form-field>
                          @if (enableAdding) {
                            <button
                              mat-icon-button
                              color="warn"
                              (click)="removeClause(rule, j)"
                              >
                              <mat-icon>delete</mat-icon>
                            </button>
                          }
                        </div>
                      }
                    </div>
                    <div class="rule-actions">
                      @if (enableAdding) {
                        <button mat-button color="warn" (click)="removeRule(i)">
                          Delete Rule
                        </button>
                      }
                    </div>
                  </div>
                </mat-expansion-panel>
              </div>
            }
          </div>
    `,
  styles: [
    `
      .rules-container {
        margin: 16px 0;
      }
      .rules-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
      }
      .header-actions {
        display: flex;
        gap: 8px;
      }
      .rule-item {
        margin-bottom: 16px;
      }
      .rule-content {
        padding: 16px 0;
      }
      .full-width {
        width: 100%;
      }
      .clauses-container {
        margin: 16px 0;
      }
      .clause-item {
        display: flex;
        gap: 16px;
        align-items: center;
        margin-bottom: 16px;
      }
      .values-field {
        flex-grow: 1;
      }
      .rule-actions {
        display: flex;
        justify-content: flex-end;
        margin-top: 16px;
      }
    `,
  ],
})
export class FeatureFlagRulesComponent implements OnChanges {
  @Input() rules: Rule[] = [];
  @Input() environment = '';
  @Input() flagKey = '';
  @Input() apiKey = '';
  @Input() enableAdding = false;
  @Output() rulesChange = new EventEmitter<Rule[]>();

  workingRules: Rule[] = [];
  hasChanges = false;

  operators: OperatorOption[] = [
    { value: 'in', label: 'is one of' },
    { value: 'endsWith', label: 'ends with' },
    { value: 'startsWith', label: 'starts with' },
    { value: 'matches', label: 'matches regex' },
    { value: 'contains', label: 'contains' },
    { value: 'lessThan', label: 'less than' },
    { value: 'lessThanOrEqual', label: 'less than or equal' },
    { value: 'greaterThan', label: 'greater than' },
    { value: 'greaterThanOrEqual', label: 'greater than or equal' },
    { value: 'before', label: 'before' },
    { value: 'after', label: 'after' },
    { value: 'segmentMatch', label: 'is in segment' },
    { value: 'semVerEqual', label: 'semantic version equals' },
    { value: 'semVerLessThan', label: 'semantic version less than' },
    { value: 'semVerGreaterThan', label: 'semantic version greater than' },
  ];

  constructor(private readonly launchDarklyService: LaunchDarklyService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rules']) {
      this.workingRules = JSON.parse(JSON.stringify(this.rules));
      this.hasChanges = false;
    }
  }

  addRule() {
    const newRule: Rule = {
      _id: crypto.randomUUID(),
      variation: 0,
      clauses: [],
      trackEvents: false,
      description: '',
      ref: '',
    };
    this.workingRules.push(newRule);
    this.markAsChanged();
  }

  removeRule(index: number) {
    this.workingRules.splice(index, 1);
    this.markAsChanged();
  }

  addClause(rule: Rule) {
    const newClause: Clause = {
      _id: crypto.randomUUID(),
      attribute: '',
      op: 'in',
      values: [],
      contextKind: 'user',
      negate: false,
    };
    rule.clauses.push(newClause);
    this.markAsChanged();
  }

  removeClause(rule: Rule, clauseIndex: number) {
    rule.clauses.splice(clauseIndex, 1);
    this.markAsChanged();
  }

  updateClauseValues(clause: Clause, valuesString: string) {
    clause.values = valuesString
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v);
    this.markAsChanged();
  }

  markAsChanged(): void {
    this.hasChanges = true;
  }

  saveChanges(): void {
    if (this.hasChanges) {
      this.launchDarklyService.updateFeatureFlagRules(
        this.apiKey,
        this.flagKey,
        this.environment,
        this.workingRules
      ).subscribe({
        next: () => {
          this.rulesChange.emit(this.workingRules);
          this.hasChanges = false;
        },
        error: (error) => {
          console.error('Error updating rules:', error);
        }
      });
    }
  }
}
